import { useState, useCallback } from 'react';
import Actions from '../../../store/Actions';
import { UseOTCBackupProps, UseOTCBackupReturn } from '../types';
import { showSnackbar } from '../../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../../context/Snackbar/Types';

export const useOTCBackup = ({
  fetchOTCData,
  clearAllIllioComparisons,
  setSelectedRows,
  showInactiveInstruments,
}: UseOTCBackupProps): UseOTCBackupReturn => {
  // Backup state
  const [isDownloadingBackup, setIsDownloadingBackup] = useState(false);
  const [isRestoringBackup, setIsRestoringBackup] = useState(false);

  // Handle download backup
  const handleDownloadBackup = useCallback(async () => {
    try {
      setIsDownloadingBackup(true); // Start loading
      await Actions.downloadOTCBackup();

      showSnackbar({
        title: 'Success',
        type: SnackbarType.success,
        message: 'Backup JSON has been downloaded successfully',
      });
    } catch (error) {
      console.error('Error downloading backup:', error);
      showSnackbar({
        title: 'Error',
        type: SnackbarType.error,
        message: 'Failed to download backup JSON',
      });
    } finally {
      setIsDownloadingBackup(false); // End loading
    }
  }, []);

  // Handle restore from backup
  const handleRestoreBackup = useCallback(async () => {
    try {
      // Create a file input element
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.json';
      fileInput.style.display = 'none';

      // Add event listener for file selection
      fileInput.addEventListener('change', async event => {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (!file) return;

        // Validate file extension
        if (!file.name.toLowerCase().endsWith('.json')) {
          showSnackbar({
            title: 'Invalid File Type',
            type: SnackbarType.error,
            message:
              'Only JSON backup files are supported. Please select a .json file.',
          });
          document.body.removeChild(fileInput);
          return;
        }

        try {
          setIsRestoringBackup(true);

          // Call the restore API
          const response = await Actions.restoreOTCFromBackup(file);

          if (response.status === 'success') {
            showSnackbar({
              title: 'Restore Successful',
              type: SnackbarType.success,
              message: `Restored ${response.restored_count} new OTC entries, updated ${response.updated_count} existing OTC entries`,
            });

            // Clear Illio comparison dots
            clearAllIllioComparisons();

            // Clear selected rows to reflect fresh state after restore
            setSelectedRows([]);

            // Refresh the OTC data
            await fetchOTCData(showInactiveInstruments);

            // Show errors if any
            if (response.errors && response.errors.length > 0) {
              const errorCount = response.errors.length;
              const hasMoreErrors = response.has_more_errors;
              showSnackbar({
                title: 'Restore Warnings',
                type: SnackbarType.warning,
                message: `${errorCount} row${
                  errorCount > 1 ? 's' : ''
                } had issues${hasMoreErrors ? ' (showing first 10)' : ''}`,
              });
            }
          } else {
            showSnackbar({
              title: 'Restore Failed',
              type: SnackbarType.error,
              message:
                response.message || 'Failed to restore OTC from JSON backup',
            });
          }
        } catch (error) {
          console.error('Error restoring backup:', error);
          showSnackbar({
            title: 'Error',
            type: SnackbarType.error,
            message:
              'Failed to restore OTC from JSON backup. Please ensure the file is a valid backup.',
          });
        } finally {
          setIsRestoringBackup(false);
          // Clean up the file input
          document.body.removeChild(fileInput);
        }
      });

      // Add to document and trigger click
      document.body.appendChild(fileInput);
      fileInput.click();
    } catch (error) {
      console.error('Error setting up file input:', error);
      showSnackbar({
        title: 'Error',
        type: SnackbarType.error,
        message: 'Failed to set up file selection',
      });
    }
  }, [
    fetchOTCData,
    clearAllIllioComparisons,
    setSelectedRows,
    showInactiveInstruments,
  ]);

  return {
    isDownloadingBackup,
    isRestoringBackup,
    handleDownloadBackup,
    handleRestoreBackup,
  };
};
